//@ts-nocheck
import { useForm } from 'react-hook-form';
import {
  FileText,
  Image,
  MessageSquare,
  CheckCircle,
  ClipboardCheck,
} from 'lucide-react';
import { DetailsTabContent } from './tabs/DetailsTab';
import { TicketDetailsNotesTab } from './tabs/NotesTab';
import { TicketDetailsCompletionTab } from './tabs/CompletionTab';
import { TicketReviewTab } from './tabs/TicketAnalysisTab';
import { PhotoUploadTab } from './tabs/PhotoUploadTab';
import { usePhotoUploads } from './hooks/usePhotoUploads';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useTicketUploads,
  useUniqueTicket,
  useUpdateTicket,
} from '@/hooks/api/useTickets';
import { useCrews } from '@/hooks/api/useCrews';
import { isValidJSON } from '@/helpers/isValidJSON';
import { usePermissions } from '@/hooks/usePermissions';
import { useDialog } from '@/providers/DialogProvider';
import { useDialogManager } from '@/hooks/useDialogManager';
import { TicketDetailsSkeleton } from '@/components/loaders/TicketDetailsSkeleton';
import { zodResolver } from '@hookform/resolvers/zod';
import { AssetTab } from './tabs/AssetTab';
import { TabbedForm } from '@/components/TabbedForm';
import { toast } from 'sonner';
import { CrewActionBanner } from './CrewActionBanner';
import { TicketClaimBanner } from './TicketClaimBanner';
import { useSearchParams } from 'react-router';
import { findActiveCrew } from '@/helpers/findActiveCrew';

interface EditTicketFormProps {
  id: string | null;
}

export const EditTicketForm = ({ id }: EditTicketFormProps) => {
  // const [searchParams, setSearchParams] = useSearchParams();
  // const activeTab = searchParams.get('tab') ?? 'details';

  // const VIEW_ONLY_TABS = new Set(['complete', 'assets', 'photos', 'review']);

  const { closeDialog } = useDialogManager();
  const { setIsDirty } = useDialog();
  const { data: ticket, isLoading, error } = useUniqueTicket(id!);
  const { isUpdating, updateTicket } = useUpdateTicket();
  const [hideTabs, setHideTabs] = useState(false);

  const { ticketUploads, isLoading: areTicketUploadsLoading } =
    useTicketUploads(id!);

  const defaultValues = useMemo(() => {
    if (!ticket) return undefined; // Return undefined while loading

    const activeCrew = findActiveCrew(ticket.ticketAssignments);

    return {
      status: ticket.status,
      priority: ticket.priority,
      assignedCrewId: activeCrew?.id || null,
      notes: ticket.notes || [],
      skillsRequired: ticket.skillsRequired?.map((s) => s.skillId),
      newNote: '',
      customFields:
        typeof ticket.customFieldValues === 'string' &&
        isValidJSON(ticket.customFieldValues)
          ? JSON.parse(ticket.customFieldValues)
          : ticket.customFieldValues || {},
      description: ticket.description || '',
    };
  }, [ticket, id]);

  const methods = useForm<any>({
    values: defaultValues,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const {
    watch,
    reset,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const { crews } = useCrews();
  const formValues = watch();

  // Permission checks for admin/crew
  const permissions = usePermissions({
    ticketStatus: ticket?.status,
    currentStatus: formValues.status,
    ticket,
  });

  // const hideFooter =
  //   !permissions.canEditTicket || VIEW_ONLY_TABS.has(activeTab);

  const handleFormSubmit = (data: any) => {
    const currentAssignedCrew = ticket?.ticketAssignments?.[0]?.crewId;
    const changedCrewId =
      data.assignedCrewId !== currentAssignedCrew ? data.assignedCrewId : null;
    const skillsInput = data.skillsRequired.filter(Boolean);

    const payload = {
      ticketId: ticket!.id,
      ticketType: data.ticketType,
      status: data.status,
      priority: data.priority,
      notes: data.notes,
      uploads: data.photos,
      crewId: changedCrewId,
      skillsRequired: skillsInput,
      customFields: JSON.stringify(data.customFields),
      description: data.description,
    };

    updateTicket(payload, {
      onSuccess() {
        reset();
        toast.success('Ticket updated!');
      },
    });
  };

  const resetFormAndClose = useCallback(() => {
    reset();
    closeDialog();
  }, [reset, closeDialog]);

  // Define tabs based on user permissions
  const tabs = useMemo(() => {
    const baseTabs = [
      {
        id: 'details',
        label: 'Details',
        icon: FileText,
        content: (
          <DetailsTabContent
            ticket={ticket!}
            crews={crews || []}
            permissions={permissions}
          />
        ),
      },
      {
        id: 'assets',
        label: 'Asset Link',
        icon: ClipboardCheck,
        content: (
          <AssetTab
            ticket={ticket}
            permissions={permissions}
            hideTabs={setHideTabs}
          />
        ),
      },
      {
        id: 'photos',
        label: 'Photos',
        icon: Image,
        badge: ticketUploads?.length || undefined,
        content: (
          <PhotoUploadTab
            ticketId={ticket?.id}
            initialPhotos={ticketUploads}
            canEdit={permissions.canEditTicket}
          />
        ),
      },
      {
        id: 'notes',
        label: 'Notes',
        icon: MessageSquare,
        badge: formValues.notes?.length || undefined,
        content: (
          <TicketDetailsNotesTab
            ticketId={id!}
            canEdit={permissions.canEditTicket}
          />
        ),
      },
    ];

    // Add the final tab based on user role
    if (permissions.isAdmin) {
      baseTabs.push({
        id: 'review',
        label: 'Review',
        icon: CheckCircle,
        content: <TicketReviewTab ticket={ticket} />,
      });
    } else {
      baseTabs.push({
        id: 'complete',
        label: 'Complete',
        icon: CheckCircle,
        content: (
          <TicketDetailsCompletionTab
            ticket={ticket}
            permissions={permissions}
            onSuccess={() => {
              reset();
              closeDialog();
            }}
          />
        ),
      });
    }

    return baseTabs;
  }, [ticket, crews, permissions, ticketUploads, formValues.notes]);

  if (isLoading) return <TicketDetailsSkeleton />;
  if (error || !ticket) return <div>Error loading ticket details.</div>;

  return (
    <div className="flex flex-col h-full min-h-0">
      {permissions.isCrew && (
        <TicketClaimBanner ticket={ticket} permissions={permissions} />
      )}
      <div className="flex-1 min-h-0">
        <TabbedForm
          methods={methods}
          onSubmit={handleFormSubmit}
          tabs={tabs}
          submitText="Update"
          cancelText="Close"
          onCancel={resetFormAndClose}
          isSubmitting={isUpdating}
          useUrlParams={true}
          urlParamName="tab"
          defaultTab="details"
          hideTabs={hideTabs}
          // onActiveTabChange={setActiveTab}
          // dont hide footer if user can edit, otherwise hide it since they can only view details
          hideFooter={!permissions.canEditTicket}
          hideFooterOnTabs={['complete', 'assets', 'photos', 'review', 'notes']}
        />
      </div>
    </div>
  );
};
